import React, { useState, useEffect, useRef } from "react";
import { Offcanvas, Badge, Input } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import DynamicTableWrapper from "./DynamicTableWrapper";
import {
  DynamicTableHelper,
  useTableHook,
  DateHelper,
} from "@workspace/common";
import {
  REPORT_INITIAL_OPTIONS,
  REPORT_MANDATORY_OPTIONS,
} from "./ReportJobConstants";
import {
  REPORT_STAGES,
  REPORT_CATEGORY,
  REPORTS_NAME,
  JOB_TYPE,
  checkFODRTypes,
} from "../UniversalReportConstant";
import { fetchJobUniversalReportJobListing } from "../../../store/actions";
import { convertJSONFileToJSONObject } from "@workspace/common/src/helpers/file_helper";

function ReportOffcanvasJob({
  isReportOffcanvasOpen,
  setIsReportOffcanvasOpen,
  selectedItem,
  filters,
  customView,
  defaultCustomView,
  setJobBubbleExternalFilter,
  jobBubbleExternalFilter,
  timelineCustomView,
}) {
  // Dispatch & Selector
  const dispatch = useDispatch();
  const reportListing = useSelector(
    (state) => state?.ReportReducer?.jobUniversalReportJobListing
  );

  // Table Config
  const [tableConfig, setTableConfig] = useState(null);

  // Calculate Ageing
  const calculateAgeing = (createdAt) => {
    const currentDate = new Date();
    const createdDate = new Date(createdAt);
    const ageingValueMS = currentDate - createdDate;
    const diffDays = Math.floor(ageingValueMS / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Custom Renders
  const customRenderList = [
    {
      names: ["jobSubmissionData.jobTitle"],
      render: (data) => (
        <div>
          <Link
            className="text-decoration-underline"
            onClick={() => {
              const win = window.open(`/jobs/${data?.id}/snapshot`, "_blank");
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.jobSubmissionData?.jobTitle}
          </Link>
        </div>
      ),
    },
    {
      names: ["jobSubmissionData.jobId"],
      render: (data) => (
        <div>
          <Link
            className="text-decoration-underline"
            onClick={() => {
              const win = window.open(`/jobs/${data?.id}/snapshot`, "_blank");
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.jobSubmissionData?.jobId
              ? data?.jobSubmissionData?.jobId
              : "-"}
          </Link>
        </div>
      ),
    },
    {
      names: ["updatedAt", "createdAt"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"
        ),
    },
    {
      names: ["jobSubmissionData.dateOpen"],
      render: (data) => (
        <div>{calculateAgeing(data?.jobSubmissionData?.dateOpen)} Days</div>
      ),
    },
    {
      names: ["jobSubmissionData.visaStatus"],
      render: (data, opt) => (
        <Badge color={"success"} className="text-uppercase">
          {DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"}
        </Badge>
      ),
    },
  ];

  const handleRowCheckMain = (jobStep, data, index, checkedData) => {
    if (checkFODRTypes(jobStep)) {
      handleRowCheck(index, checkedData);
    } else {
      handleRowCheck(data?.id, checkedData);
    }
  };

  const handleSelectAllRow = (jobStep, dataChecked) => {
    if (checkFODRTypes(jobStep)) {
      selectAllRows(dataChecked, true);
    } else {
      selectAllRows(dataChecked);
    }
  };

  // Setup
  const generateReportConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      {
        header: (
          <div className="form-check px-0">
            <Input
              className="form-check-input"
              type="checkbox"
              id="checkbox"
              checked={
                activeRow?.length > 0 && activeRow?.length === tableData?.length
              }
              onChange={(e) => {
                handleSelectAllRow(jobStep, e?.target?.checked);
              }}
            />
          </div>
        ),
        name: "checkbox",
        sort: false,
        render: (data, index) => {
          return (
            <div className="form-check px-0">
              <Input
                className="form-check-input"
                type="checkbox"
                name="chk_child"
                checked={
                  checkFODRTypes(jobStep)
                    ? activeRow?.includes(parseInt(index))
                    : activeRow?.includes(parseInt(data?.id))
                }
                onChange={(e) =>
                  handleRowCheckMain(jobStep, data, index, e.target.checked)
                }
              />
            </div>
          );
        },
      },
      ...customConfig,
    ];
  };

  // Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    activeRow,
    setActiveRow,
    handleRowCheck,
    selectAllRows,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
    },
    REPORT_MANDATORY_OPTIONS,
    REPORT_INITIAL_OPTIONS,
    customRenderList,
    generateReportConfig
  );

  const hasPageRendered = useRef(false);
  const [jobStep, setJobStep] = useState("");

  const getJobStep = (reportCategory, reportName) => {
    switch (reportCategory) {
      case REPORT_CATEGORY.JOBS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_JOBS:
            return JOB_TYPE.TOTAL_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS]
            .TOTAL_JOBS_ASSIGNED_TO_RECRUITERS:
            return JOB_TYPE.ALL_ASSIGNED_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_ACTIVE_JOBS:
            return JOB_TYPE.ACTIVE_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_INACTIVE_JOBS:
            return JOB_TYPE.INACTIVE_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_CLOSED_WIN_JOBS:
            return JOB_TYPE.CLOSED_WIN_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_CLOSED_LOST_JOBS:
            return JOB_TYPE.CLOSED_LOST_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].NEW_JOBS:
            return JOB_TYPE.NEW_JOB;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].NO_SUBMISSIONS:
            return JOB_TYPE.NO_SUBMISSIONS;
        }
      case REPORT_CATEGORY.JOBS_FOD:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_TOTAL:
            return JOB_TYPE.FOD_TOTAL;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_ACTION_TAKEN:
            return JOB_TYPE.FOD_ACTION_TAKEN;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_NO_ACTION:
            return JOB_TYPE.FOD_NO_ACTION;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_TOTAL_R:
            return JOB_TYPE.FOD_TOTAL_R;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_ACTION_TAKEN_R:
            return JOB_TYPE.FOD_ACTION_TAKEN_R;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_NO_ACTION_R:
            return JOB_TYPE.FOD_NO_ACTION_R;
        }
      case REPORT_CATEGORY.GROWTH_RATIO:
      case REPORT_CATEGORY.PROFILES:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].TAG:
            return REPORT_STAGES.TAGGED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].ASSOCIATED:
            return REPORT_STAGES.ASSOCIATED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].SUBMITTED_TO_SALES:
            return REPORT_STAGES.SUBMIT_TO_SALES;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].SUBMITTED_TO_CLIENT:
            return REPORT_STAGES.SUBMIT_TO_CLIENT;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].PROFILE_FEEDBACK_PENDING:
            return REPORT_STAGES.PROFILE_FEEDBACK;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].CANDIDATE_WITHDRAWN:
            return REPORT_STAGES.PROFILE_WITHDRAWN;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].PROFILE_REJECTED_BY_SALES:
            return REPORT_STAGES.SUBMIT_TO_SALES_REJECTED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .PROFILE_REJECTED_BY_CLIENT:
            return REPORT_STAGES.SUBMIT_TO_CLIENT_REJECTED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].NO_SUBMISSIONS:
            return REPORT_STAGES.NO_SUBMISSION;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].REQUIREMENTS_WORKED_ON:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .PROFILE_SUBMITTED_TO_INTERVIEWS_HAPPENED:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .INTERVIEW_HAPPENED_TO_SELECTED:
            return;
          default:
            return;
        }
      case REPORT_CATEGORY.INTERVIEWS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].SCHEDULED_INTERVIEW:
            return REPORT_STAGES.INTERVIEW_SCHEDULED;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].RESCHEDULE_INTERVIEW:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].INTERVIEW_HAPPENED:
            return REPORT_STAGES.INTERVIEW_HAPPENED;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS]
            .INTERVIEW_BACKOUT_BY_CANDIDATE:
            return REPORT_STAGES.INTERVIEW_BACK_OUT;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS]
            .INTERVIEW_REJECTED_BY_CLIENT:
            return REPORT_STAGES.INTERVIEW_REJECTED;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].UPCOMING_INTERVIEWS:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].UNASSIGNED_REQUIREMENTS:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].NEW_JOB_TO_SELECTED:
            return REPORT_STAGES.CONDITIONAL_OFFER_ACCEPTED;
          default:
            return;
        }
      case REPORT_CATEGORY.CONDITIONAL_OFFERS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].PREPARED:
            return REPORT_STAGES.CONDITIONAL_OFFER_PREPARED;
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].RELEASED:
            return REPORT_STAGES.CONDITIONAL_OFFER_RELEASED;
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].ACCEPTED:
            return REPORT_STAGES.CONDITIONAL_OFFER_ACCEPTED;
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].REJECTED:
            return REPORT_STAGES.CONDITIONAL_OFFER_REJECTED;
          default:
            return;
        }
      case REPORT_CATEGORY.TOS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.TOS].PREPARED:
            return REPORT_STAGES.TOS_PREPARED;
          case REPORTS_NAME[REPORT_CATEGORY.TOS].APPROVED:
            return REPORT_STAGES.TOS_APPROVED;
          default:
            return;
        }
      default:
        return;
    }
  };

  const getJobLabel = (reportCategory, reportName) => {
    switch (reportCategory) {
      case REPORT_CATEGORY.JOBS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS]
            .TOTAL_JOBS_ASSIGNED_TO_RECRUITERS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS]
              .TOTAL_JOBS_ASSIGNED_TO_RECRUITERS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_ACTIVE_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_ACTIVE_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_INACTIVE_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_INACTIVE_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_CLOSED_WIN_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_CLOSED_WIN_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_CLOSED_LOST_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].TOTAL_CLOSED_LOST_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].NEW_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].NEW_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS]
            .TOTAL_HEAD_COUNTS_POSITIONS_ACTIVE_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS]
              .TOTAL_HEAD_COUNTS_POSITIONS_ACTIVE_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS]
            .TOTAL_HEAD_COUNTS_POSITIONS_NEW_JOBS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS]
              .TOTAL_HEAD_COUNTS_POSITIONS_NEW_JOBS;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS].NO_SUBMISSIONS:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS].NO_SUBMISSIONS;
        }
      case REPORT_CATEGORY.JOBS_FOD:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_TOTAL:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_TOTAL;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_ACTION_TAKEN:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_ACTION_TAKEN;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_NO_ACTION:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_NO_ACTION;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_TOTAL_R:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_TOTAL_R;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_ACTION_TAKEN_R:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_ACTION_TAKEN_R;
          case REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_NO_ACTION_R:
            return REPORTS_NAME[REPORT_CATEGORY.JOBS_FOD].FOD_NO_ACTION_R;
        }
      default:
        return;
    }
  };

  // Set Job Step
  useEffect(() => {
    if (selectedItem) {
      setJobStep(getJobStep(selectedItem?.key, selectedItem.value));
    }
  }, [selectedItem]);

  useEffect(() => {
    if (jobStep !== "" && filters !== null) {
      dispatch(
        fetchJobUniversalReportJobListing(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
            jobType: jobStep,
            filters: jobBubbleExternalFilter,
          })
        )
      );
    }
  }, [pageRequest, jobStep, jobBubbleExternalFilter]);

  useEffect(() => {
    if (reportListing) {
      setPageInfoData(reportListing);
      setTableData(reportListing?.jobs);
    }
  }, [reportListing, jobStep]);

  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateReportConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig.find(
          (oldItem) => oldItem?.name === item?.name
        );
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateReportConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // Check if page has re-rendered
  useEffect(() => {
    if (hasPageRendered.current) {
      pageRequestSet.setPage(0);
    }
    hasPageRendered.current = true;
  }, []);

  return (
    <React.Fragment>
      <Offcanvas
        // backdrop={false}
        direction="bottom"
        isOpen={isReportOffcanvasOpen}
        toggle={() => setIsReportOffcanvasOpen(!isReportOffcanvasOpen)}
        style={{ height: "70vh", borderTop: "1px solid lightgray" }}
      >
        <div className="offcanvas-body" style={{ overflowY: "hidden" }}>
          <DynamicTableWrapper
            data={reportListing?.jobs ?? []}
            config={tableConfig}
            pageInfo={pageInfo}
            pageRequest={pageRequest}
            pageRequestSet={pageRequestSet}
            search={search}
            setSearch={setSearch}
            // optGroup={candidatesFields}
            setCustomConfigData={setCustomConfigData}
            header="Universal Report"
            activeRow={activeRow}
            setActiveRow={setActiveRow}
            setTableConfig={setTableConfig}
            jobStep={jobStep}
            isReportOffcanvasOpen={isReportOffcanvasOpen}
            setIsReportOffcanvasOpen={setIsReportOffcanvasOpen}
            tableTitle={getJobLabel(selectedItem?.key, selectedItem?.value)}
            setJobBubbleExternalFilter={setJobBubbleExternalFilter}
            jobBubbleExternalFilter={jobBubbleExternalFilter}
            customView={customView}
            timelineCustomView={timelineCustomView}
          />
        </div>
      </Offcanvas>
    </React.Fragment>
  );
}

export default ReportOffcanvasJob;
